
import Vue from "vue";
import {ProfiControlUser} from "@/model/ProfiControlUser";
import {ErrorCodes, roles} from "@/Enums"
import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";
import BaseButton from "@/components/buttons/BaseButton.vue";
import ConfirmationDialog from "@/components/base/ConfirmationDialog.vue";
import InformationDialog from "@/components/base/InformationDialog.vue";
import {
  createProficontrolUser,
  deleteProficontrolUser,
  getProficontrolUsers,
  GetProficontrolUsersParams,
  patchProficontrolUser,
  ProfiControlUserRequester
} from "@/scripts/ProfiControlRequests/ProfiControlUserRequester";
import { buildQueryString, debounce, HelperFunctions } from "@/scripts/Common/HelperFunctions";
import {ErrorResult} from "@/model/ErrorResult";
import { DynamicFilterBuilder, FilterConfiguration, FilterType  } from "@/components/base/dynamicFilterBuilder/";

export default Vue.extend({
  components: {InformationDialog, ConfirmationDialog, BaseButton, FloatingActionButton, DynamicFilterBuilder},
  props: ["Token", "HasEditRights", "HasCertificateRights"],
  data() {
    return{
    users: new Array<ProfiControlUser>(),
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    sorting: {
      sortBy: "" as string | undefined,
      sortDesc: false as boolean | undefined,
    },
    isLoading: false,
    selectedUser: ProfiControlUser.CreateDummy(),
    updateDialog: false,
    responseSucceded: false,
    requestErrorResult: new ErrorResult,
    valid: true,
    showPassword: false,
    showPasswordConfirmation: false,
    passwordRules: {
      MaxLength:20,
      MinLength: 4,
      MinCountEnoughNumbers: 1,
      MinCountUpperChars: 1,
      MinCountLowerChars: 1,
      MinCountSpecialSigns: 1,
      Valid: true,
    },
    certificateView: null,
    createNewCertificateDialog: false,
    certificateLink: "",
    format: /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g,
    errorDialog:{
      headline: "",
      message: ""
    },
    filterValues: {
      username: "" as string | null,
      firstname: "" as string | null,
      lastname: "" as string | null
    },
    dynamicFilterConfig: [] as FilterConfiguration[],
}},
  computed: {
    HasNoEditRights() : boolean
    {
      return this.HasEditRights == false;
    },
    HasNoCertificateId() : boolean
    {
      return this.selectedUser.CertificateId == undefined;
    },
    IsNewUser() : boolean
    {
      return this.selectedUser.IsNew;
    },
    headers() : any
    {
      return [
        { value: "actions", sortable: false, width: "1%" },
        { text: this.$t("User.UserName"), align: "start", value: "UserName" },
        { text: this.$t("User.FirstName"), value: "FirstName" },
        { text: this.$t("User.LastName"), value: "LastName" },
        { text: this.$t("User.Description"), value: "Description", sortable: false },
        { text: this.$t("User.RoleField"), value:"Role", sortable: false }
      ];
    },
    rules() {
      return {
        required: (value: string) =>
            (!this.IsNewUser && !value) ||
            !!value ||
            this.$t('validationError.required'),
        sameValue:(valueA: string, valueB: string) =>
            valueA === valueB ||
            this.$t('validationError.passwordConfirmationInvalid'),
        max: (maxLength: number, value: string) =>
            value == undefined ||
            value.length <= maxLength ||
            this.$t('validationError.toLong', { maxLength: maxLength }),
        min: (minLength: number, value: string) =>
            value == undefined ||
            value.length >= minLength ||
            this.$t('validationError.toShort', { minLength: minLength }),
        enoughNumbers: (minNumberCount: number, value: string) =>
            value == undefined ||
            (value.match(/\d/g) || []).length >= minNumberCount ||
            this.$t('validationError.notEnoughNumbers', { minNumberCount: minNumberCount }),
        enoughUpperCases: (minUpperCases: number, value: string) =>
            value == undefined ||
            (value.match(/[A-Z]/g) || []).length >= minUpperCases ||
            this.$t('validationError.notEnoughUpperChars', { minUpperCases: minUpperCases }),
        enoughLowerCases: (minLowerCases: number, value: string) =>
            value == undefined ||
            (value.match(/[a-z]/g) || []).length >= minLowerCases ||
            this.$t('validationError.notEnoughLowerChars', { minLowerCases: minLowerCases }),
        enoughSpecialSigns: (minSpecialSigns: number, value: string) =>
            value == undefined ||
            (value.match(this.format) || []).length >= minSpecialSigns ||
            this.$t('validationError.notEnoughSpecialSigns', { minSpecialSigns: minSpecialSigns })
      };
    },
    roles(): Array<any> {
      return [
        {id: roles.None, value: this.$t("User.Roles.None")},
        {id: roles.Administrator, value: this.$t("User.Roles.Admin")},
        {id: roles.Hauptbenutzer, value: this.$t("User.Roles.MainUser")},
        {id: roles.Benutzer, value: this.$t("User.Roles.User")},
        {id: roles.BenutzerKunde, value: this.$t("User.Roles.UserCustomers")},
        {id: roles.BenutzerGeber, value: this.$t("User.Roles.UserIssuers")},
        {id: roles.BenutzerFinanz, value: this.$t("User.Roles.UserFinanz")},
        {id: roles.BenutzerAussen, value: this.$t("User.Roles.FieldServiceUser")}
      ]
    },
  },
  created: function () {
    this.dynamicFilterConfig = [
      {
        type: "input",
        props: {
          label: this.$t("User.UserName"),
        },
        colProps: { cols: 12, sm: 4, md: 4, lg: 4 },
      },
      {
        type: "input",
        props: {
          label: this.$t("User.FirstName"),
        },
        colProps: { cols: 12, sm: 4, md: 4, lg: 4 },
      },
      {
        type: "input",
        props: {
          label: this.$t("User.LastName"),
        },
        colProps: { cols: 12, sm: 4, md: 4, lg: 4 },
      },
    ];

    this.LoadProficontrolUsers();
  },
  methods: {
//---------------------------------------------------------User Handlers-------------------------------------------------------
    onFilterInput(values: Array<string|null>) {
      this.filterValues.username = values[0];
      this.filterValues.firstname = values[1];
      this.filterValues.lastname = values[2];
    },
    selectItem: function (user: ProfiControlUser) {
      this.selectedUser = user;
      this.ValidateUserForm();
      this.CertificateViewChanger();
    },
    GetUserRoleString(role : roles){
        const roleMap = new Map([
              [roles.None, this.$t("User.Roles.None")],
              [roles.Administrator, this.$t("User.Roles.Admin")],
              [roles.Hauptbenutzer, this.$t("User.Roles.MainUser")],
              [roles.Benutzer, this.$t("User.Roles.User")],
              [roles.BenutzerKunde, this.$t("User.Roles.UserCustomers")],
              [roles.BenutzerGeber, this.$t("User.Roles.UserIssuers")],
              [roles.BenutzerFinanz, this.$t("User.Roles.UserFinanz")],
              [roles.BenutzerAussen, this.$t("User.Roles.FieldServiceUser")]
            ]);
        return roleMap.get(role);
    },
    Add: function () {
      const user = new ProfiControlUser();
      this.users.push(user);
      this.selectItem(user);
      this.CertificateViewChanger();
    },
    Save: async function () {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/proficontroluser`
      const [proficontrolUser, errorResult] = this.selectedUser.IsNew
          ? await createProficontrolUser(url, this.Token, this.selectedUser)
          : await patchProficontrolUser(url, this.Token, this.selectedUser)

      this.requestErrorResult = errorResult
      this.selectedUser.SetValues(proficontrolUser)
      this.selectedUser.Reset()
    },
    RemoveSelectedUser: function () {
      const index = this.users.indexOf(this.selectedUser);
      this.users.splice(index, 1);
      this.users.length > 0
          ? this.selectItem(this.users[0])
          : this.selectedUser = ProfiControlUser.CreateDummy();
    },
    async DeleteItemConfirm() {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/proficontroluser?profiControlUserId=${this.selectedUser.Id}`
      if (!this.selectedUser.IsNew) {
        this.requestErrorResult = await deleteProficontrolUser(url, this.Token);
      }
      this.RemoveSelectedUser();
      this.ResetUserValidation();
    },
    Reset: function () {
      this.selectedUser.IsNew
        ? this.RemoveSelectedUser()
        : this.selectedUser.Reset();
      this.ResetUserValidation();
    },
    async LoadProficontrolUsers() {

      const queryParams : GetProficontrolUsersParams = {
        ...this.filterValues,
        pageNumber: this.page - 1,
        pageSize: this.itemsPerPage,
        sortColumn: this.sorting.sortBy ? this.sorting.sortBy : undefined,
        sortDirection: this.sorting.sortDesc !== undefined ? this.sorting.sortDesc ? "desc" : "asc" : undefined
      };

      let queryString = buildQueryString(queryParams);

      const url = `${this.$config.GatewayUrl}/api/v1/admin/proficontroluser/list?${queryString}`

      this.isLoading = true;
      try {
        const [paginationCount, proficontrolUserDtoList] = await getProficontrolUsers(url, this.Token);
        const fetchedUsers = new Array<ProfiControlUser>();
        proficontrolUserDtoList.forEach(user => fetchedUsers.push(new ProfiControlUser(user)));
        this.pageCount = paginationCount
        this.users = fetchedUsers;
      } finally {
        this.isLoading = false;
      }
    },

//---------------------------------------------------------End User Handlers-------------------------------------------------------

//---------------------------------------------------------Certificate Handlers-------------------------------------------------------
    DownloadCertificate: function () {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/certificate?profiControlUserId=${this.selectedUser.Id}`
      ProfiControlUserRequester.DownloadCertificate(url, this.Token, this.selectedUser);
    },
    RevokeCertificate: function () {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/certificate/revoke?profiControlUserId=${this.selectedUser.Id}`
      ProfiControlUserRequester.RevokeCertificate(url, this.Token, this.selectedUser);
    },
    CreateNewCertificate: function () {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/certificate`
      ProfiControlUserRequester.CreateCertificate(url, this.Token, this.selectedUser);
    },
    Delete: function (user: ProfiControlUser) {
      this.selectedUser = user;
      (this.$refs.deleteProfiControlUserDialog as any).Show();
    },
    CreateInitialCertificateDownloadLink: function() {
      const url = `${this.$config.GatewayUrl}/api/v1/admin/certificate/token/create?profiControlUserId=${this.selectedUser.Id}`
      const baseDownloadLink = `${this.$config.CertificateDownloadUrl}?token=`
      ProfiControlUserRequester.CreateDownloadLink(url, this.Token, this.selectedUser, baseDownloadLink);
    },
//---------------------------------------------------------End Certificate Handlers-------------------------------------------------------

//---------------------------------------------------------Validations Handlers-------------------------------------------------------
    ResetUserValidation() {
      (this.$refs.userForm as any).resetValidation();
    },

    ValidateUserForm() {
      (this.$refs.userForm as any).validate();
    },
//---------------------------------------------------------End Validations Handlers-------------------------------------------------------

//---------------------------------------------------------View Handlers-------------------------------------------------------
    CertificateViewChanger(){
      (this.selectedUser.CertificateId == undefined || this.IsNewUser)
        ? this.$data.certificateView = {
            items: [
              {  icon: 'mdi-plus-box', title: this.$t("Certificate.Create") , action: this.OpenNewCertificateDialog},
            ],
            icon: 'mdi-close-octagon',
            type: 'warning',
            color: 'blue-grey darken-1'
          }
        : this.$data.certificateView = {
            items: [
              {  icon: 'mdi-close-octagon', title: this.$t("Certificate.Revoke") , divider: true , action: this.RevokeCertificate},
              {  icon: 'mdi-download', title: this.$t("Certificate.Download") , divider: true , action: this.DownloadCertificate},
              {  icon: 'mdi-content-copy', title: this.$t("Certificate.CreateLink") , divider: true , action: this.CreateInitialCertificateDownloadLink},
            ],
            icon: 'mdi-check-bold',
            type: 'success',
            color: 'green lighten-2'
          }
    },
    ResponseSucceded: function (){
      this.responseSucceded = true;
      setTimeout(() => {
        this.responseSucceded = false
      }, 5000)
    },
    OpenNewCertificateDialog: function(){
      this.createNewCertificateDialog = true;
      this.selectedUser.Password = "";
      this.selectedUser.PasswordConfirmation = "";
    },
//---------------------------------------------------------End View Handlers-------------------------------------------------------
    ShowInformationDialog: function (){
      (this.$refs.informationDialog as any).Show();
    },
    ResetErrorDialog(){
      this.errorDialog.headline = "";
      this.errorDialog.message = "";
    },
    handleFilterChange() {
      if (this.page === 1) {
        this.LoadProficontrolUsers();
      } else {
        this.page = 1; // will trigger reload
      }
    },
  },
  watch: {
    page: function (){
      this.LoadProficontrolUsers()
    },
    'selectedUser.CertificateId': function () {
      this.createNewCertificateDialog = false;
      this.CertificateViewChanger();
    },
    'selectedUser.CertificateDownloadLink': function (){
      if(!!this.selectedUser.CertificateDownloadLink)
      {
        HelperFunctions.CopyText(this.selectedUser.CertificateDownloadLink);
        this.selectedUser.ResetDownloadToken();
        this.ResponseSucceded();
      }
    },
    'selectedUser.HasCertificateError': function (){
      if(!this.selectedUser.HasCertificateError){
        return;
      }
      this.errorDialog.headline = this.$t('Dialogs.CertificateError.Headline').toString();
      this.errorDialog.message = this.$t('Dialogs.CertificateError.Content').toString();
      this.ShowInformationDialog();
      this.selectedUser.ResetModelErrors();
    },
    'requestErrorResult.hasError': function (){
      switch (this.requestErrorResult.errorCode){
        case ErrorCodes.duplicate_username: {
          this.errorDialog.headline = this.$t('Dialogs.Duplicate.Headline').toString()
          this.errorDialog.message = this.$t('Dialogs.Duplicate.Content').toString()
          break
        }
        case ErrorCodes.not_found: {
          this.errorDialog.headline = this.$t('Dialogs.Duplicate.Headline').toString()
          this.errorDialog.message = this.$t('Dialogs.Duplicate.Content').toString()
          break
        }
        default: {
          this.errorDialog.headline = this.$t('Dialogs.Duplicate.Headline').toString()
          this.errorDialog.message = this.$t('Dialogs.Duplicate.Content').toString()
        }
      }

      this.ShowInformationDialog();
      this.requestErrorResult.hasError = false;
    },
    filterValues: {
      handler: debounce(function(this: any) {
        this.handleFilterChange();
      }),
      deep: true
    },
    sorting: {
      handler: debounce(function(this: any) { // avoid double request if both sortBy and sortDesc are changed at the same time
        this.LoadProficontrolUsers();
      }, 0),
      deep: true
    }
  },
});
